import React from "react";
import * as Scrivito from "scrivito";
import { Table } from "reactstrap";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ProductListTableRowSkeleton } from "components/ProductListOfProject/SubComponents/ProductListTableRowSkeleton";
import { useTranslate } from "hooks";

type VirtualizedModalTableColumn = {
  label: React.ReactNode;
  width?: React.CSSProperties["width"];
};

type VirtualizedModalTableProps<TItem> = {
  columns: VirtualizedModalTableColumn[];
  products: TItem[];
  renderRow: (item: TItem) => React.ReactNode;
  fetchingProductList: boolean;
  skeletonCount: number;
  estimateSize?: number;
  overscan?: number;
  isLastPage?: boolean;
  tableRef?: React.RefObject<HTMLDivElement | null>;
  isModalView?: boolean;
};

const VirtualizedModalTableComp = <TItem,>({
  columns,
  products,
  renderRow,
  fetchingProductList,
  skeletonCount,
  estimateSize = 58,
  overscan = 7,
  isLastPage,
  tableRef,
  isModalView,
}: VirtualizedModalTableProps<TItem>) => {
  const translate = useTranslate();
  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: !fetchingProductList ? products.length : skeletonCount,
    getScrollElement: () => tableRef?.current ?? null,
    estimateSize: () => estimateSize,
    overscan,
  });

  const totalHeight = isLastPage
    ? rowVirtualizer.getTotalSize() + estimateSize
    : rowVirtualizer.getTotalSize();

  return (
    <div
      ref={tableRef}
      style={{
        overflowY:
          (!fetchingProductList && products.length === 0) || isModalView
            ? "hidden"
            : "auto",
        height: !fetchingProductList && products.length === 0 ? "auto" : "100%",
      }}
    >
      <Table>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                className="px-2 py-1 bg-white position-sticky"
                key={index}
                style={{
                  width: column.width,
                  top: 0,
                  zIndex: 1,
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody
          style={{
            height: `${totalHeight}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {rowVirtualizer.getVirtualItems().length > 0 ? (
            <>
              {rowVirtualizer.getVirtualItems().map((virtualItem) => (
                <tr
                  key={virtualItem.index}
                  style={{
                    position: "absolute",
                    display: "flex",
                    width: "100%",
                    height: `${estimateSize}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {!fetchingProductList ? (
                    renderRow(products[virtualItem.index])
                  ) : (
                    <ProductListTableRowSkeleton />
                  )}
                </tr>
              ))}
              <tr
                key="last-row"
                style={{
                  position: "absolute",
                  display: "flex",
                  width: "100%",
                  height: `${estimateSize}px`,
                  transform: `translateY(${rowVirtualizer.getTotalSize()}px)`,
                }}
              ></tr>
            </>
          ) : (
            <tr>
              <td
                className="text-center"
                colSpan={6}
                style={{ height: "55vh" }}
              >
                {translate("PROJECT_LIST_NO_DATA")}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export const VirtualizedModalTable = Scrivito.connect(
  VirtualizedModalTableComp,
) as typeof VirtualizedModalTableComp;
